// The validator's wire types and the pure logic the drawer needs.
//
// Everything here mirrors `validator/validation/` — the Python side owns the
// shapes, the thresholds and the Romanian wording, and this file adds no numbers
// of its own. That is deliberate: `inspect.ts` still carries hand-copied
// constants (`EVAC_TWO_DIR_M = 42`, and see the comment there about having first
// written 40) and each one is a chance for the interface to quote a rule it is
// not actually checking against. A validation row prints `required` and
// `measured` as the server computed them, so a drift shows up as two numbers
// rather than as a reassuring label.
//
// The rendering logic lives in ValidatorDrawer.tsx; the grouping and counting
// live here, as plain functions over plain data, so they can be tested without a
// DOM (the same split `inspect.ts`/`Inspector.tsx` already uses).

import type { Selection } from "./inspect";

// --------------------------------------------------------------------------
// Statuses
// --------------------------------------------------------------------------

/** Five outcomes, not two. The middle three are where most of the value is:
 *  "this rule applies HERE and someone must confirm it" is more actionable than
 *  a rule quietly missing from the list. */
import { limba, t, tn } from "../i18n";
import type { Cheie, CheiePlural } from "../i18n";

export type VStatus =
  | "conform"
  | "neconform"
  | "necesita_confirmare"
  | "inaplicabil"
  | "neverificabil";

const STATUS_CHEIE: Record<VStatus, Cheie> = {
  conform: "verdict.conform",
  neconform: "verdict.neconform",
  necesita_confirmare: "verdict.necesita_confirmare",
  inaplicabil: "verdict.inaplicabil",
  neverificabil: "verdict.neverificabil",
};

/** Verdictul, în limba ecranului. */
export const numeleVerdictului = (st: VStatus): string => t(STATUS_CHEIE[st]);

/** DISPLAY order, worst first. Mirrors `validation/model.py STATUS_ORDER` — the
 *  two must agree or the drawer's ordering fights the server's. A row that says
 *  "I cannot answer" sits under the rows that answered. */
export const STATUS_ORDER: Record<VStatus, number> = {
  neconform: 0,
  necesita_confirmare: 1,
  conform: 2,
  inaplicabil: 3,
  neverificabil: 4,
};

/** ROLL-UP order. Mirrors `validation/model.py ROLLUP_ORDER`, and it is a
 *  different ranking on purpose: when several verdicts collapse into one badge,
 *  an element nobody could measure must not be hidden behind the ones that
 *  passed. Under the display order `conform` (2) beat `neverificabil` (4), so a
 *  group holding one measured pass and one unmeasurable element wore a green
 *  badge. Used by `worst`; never for sorting. */
export const ROLLUP_ORDER: Record<VStatus, number> = {
  neconform: 0,
  neverificabil: 1,
  necesita_confirmare: 2,
  conform: 3,
  inaplicabil: 4,
};

/** Statuses that mean "the validator actually looked and had something to say". */
export const DECIDED: VStatus[] = ["neconform", "necesita_confirmare", "conform"];

export const isFailure = (s: VStatus) => s === "neconform";
export const needsAttention = (s: VStatus) =>
  s === "neconform" || s === "necesita_confirmare";

// --------------------------------------------------------------------------
// Wire shapes
// --------------------------------------------------------------------------

/** Where the act a citation names can be read, and WHERE IT STANDS in the
 *  citation string.
 *
 *  `start`/`end` index into the `norm` text, so the reference is underlined in
 *  place rather than repeated underneath as a chip. The offsets come from the
 *  server for the same reason the thresholds do: matching an act against a
 *  catalogue is work on the architect's own wording, and this file adds no data of
 *  its own.
 *
 *  The span covers WHAT THE LINK POINTS AT, which is not always just the act. A
 *  citation naming a subdivision the portal anchors is underlined whole — "*Legea
 *  114/1996, anexa nr. 1, secț. F*" — because the link jumps there; one that names
 *  a subdivision with no anchor is underlined only on the act — "*P118/1-2025*,
 *  art. 2.3.2.1.7" — because that is as far as the link goes. Underline and
 *  destination always agree.
 *
 *  Only acts whose official text was verified to resolve get an entry, from one of
 *  two official publishers: the legislative portal for laws and orders, and the
 *  issuing ministry for the technical regulations the portal does not carry. A
 *  citation naming something nobody publishes free — the ASRO standards are sold —
 *  carries no link and renders as plain text, which is how it always did. See
 *  `validator/validation/norms.py`. */
export interface VNormLink {
  /** The whole reference the link resolves, as the architect wrote it: the act
   *  plus the subdivision when the link reaches one. This is what `start`/`end`
   *  bound and what gets underlined. */
  text: string;
  /** Just the act, as written. Differs from `text` exactly when the link goes
   *  deeper than the act's front door. */
  act: string;
  id: string;
  title: string;
  url: string;
  /** The destination is a FILE, not a page: the ministry's regulations are scanned
   *  PDFs of 4 to 23 MB. The interface says so before the click, because a
   *  citation that quietly starts a download is a link that misbehaves even when
   *  it goes exactly where it should. */
  is_pdf?: boolean;
  start: number;
  end: number;
}

export interface VRequired {
  kind: "min" | "max" | "range" | "table" | "none";
  value: number | null;
  value_max: number | null;
  unit: string;
  display: string;
}

/** An annotation the plan draws to show WHY. Metre space, same coordinates the
 *  plan is drawn in — `Editor` applies only translate+scale, so these land on
 *  the drawing untransformed.
 *
 *  `context` holds Selections, never geometry: the drawer already has every
 *  polygon on screen, and re-sending the hall ring with each of its instances is
 *  what made one project's report 6,3 MB. `region` is a rect for geometry the
 *  validator derived and the UI cannot look up — a hall's individual stretch. */
export interface VAnno {
  type: "dim" | "outline" | "path" | "marks";
  label: string;
  /** dim: the two ends of the dimension line. */
  a?: [number, number];
  b?: [number, number];
  /** path: the walked route — connected, directed, in this order.
   *  marks: discrete places, numbered in this order and joined by nothing. The
   *  two share the field because `type` already says which it is, and the
   *  difference between them is what the drawing asserts, not what it carries. */
  pts?: [number, number][];
  /** outline: a derived ring, present only when it is not the instance's own ref. */
  poly?: [number, number][];
  /** marks: the line network the marks came from — aisle centrelines under a set
   *  of conflict points — drawn faintly beneath them. Sent as coordinates because
   *  a lane is not a `Selection` kind and the UI has no way to look one up. */
  segs?: [number, number][][];
  /** A RING to tint — a corridor stretch, or where a route pinches.
   *
   *  Not an [x0,y0,x1,y1] rect. Stretches are rectangles in their BUILDING's
   *  frame, so in a block standing at 43° they are rotated in world space and an
   *  axis-aligned box drawn around one also covers the rooms beside it. */
  region?: [number, number][];
  /** Other elements to tint faintly, as Selections. */
  context?: Selection[];
}

/** What an instance points at: the viewer's own `Selection`, plus the canonical
 *  document's stable `id` for the same object.
 *
 *  Both, and in that order of authority. The id is the identity — stable across a
 *  re-solve, and the key the architect's other tools will use. The indices are
 *  how THIS renderer finds the object, and a position is not a name: a report
 *  held across a re-solve would point `i: 4` at whatever is now fifth and
 *  highlight the wrong flat, in silence. So `selectionOfId` resolves the id
 *  first and the indices are the fallback for a legacy document, which has none.
 *
 *  `id` is scoped BY LEVEL, not global: a canonical document stores one
 *  residential storey and repeats it, so all eight physical storeys share the
 *  same entity ids. `rm_d1f68…` names a room on the stored storey; `(level, id)`
 *  names the room a person walks into. */
export type VRef = Selection & { id?: string };

export interface VInstance {
  /** An EXISTING plan selection, with the document id beside it. Handed straight
   *  to `setSelection`, so the plan highlights it with machinery that is already
   *  there — no element has to be re-drawn or made newly pickable. */
  ref: VRef;
  label: string;
  status: VStatus;
  detail_ro: string;
  measured?: number;
  required?: number;
  unit?: string;
  /** The sub-element the annotation is about, where that is finer than anything
   *  the plan can select: a hall stretch, a door, a route, a wall. */
  /** The sub-element the annotation is about. `id` means this part IS that
   *  document entity (only a `door` has one); `of` means it was DERIVED from that
   *  entity and has no identity of its own — a hall stretch is `of` the hall plus
   *  an ordinal. Nothing here is ever a synthesised id: one would look exactly as
   *  durable as a real one and would not be. */
  part?: {
    kind: "hallSegment" | "door" | "route" | "wall";
    /** Present only when the part is itself a document entity. */
    id?: string;
    /** The entity this part was derived from. */
    of?: string;
    door_id?: string;
    from_id?: string;
    toward_id?: string;
    index?: number;
    toward?: string;
    segments?: number[];
    pinch?: number | null;
    [k: string]: unknown;
  };
  bbox?: [number, number, number, number];
  anno?: VAnno;
}

export interface VRule {
  id: string;
  code: string;
  source: string;
  chapter: string;
  condition_ro: string;
  requirement_ro: string;
  norm: string;
  /** Prose: what one instance IS, in Romanian, for the caption under the title.
   *  Not an identifier — nothing keys off it and no two rules have to agree on
   *  it. The identifier is `base_kind`. */
  applies_to: string;
  /** The `Selection` kind every instance's `ref` points at, and therefore the
   *  unit conformity is counted in. Empty on rules with no check. */
  base_kind: string;
  /** Plural Romanian nouns for the tally: "0 din 112 holuri · 112 din 224
   *  tronsoane". `part_label` is empty when a rule yields one instance per
   *  element, which is most of them. */
  base_label: string;
  part_label: string;
  required: VRequired;
  fixed_status: VStatus | null;
  reason_ro: string;
  /** Rules THIS rule's own DACĂ/ATUNCI names — a dependency the document states,
   *  so the direction is the document's and not ours. Shown as "Depinde de". */
  depends_on: string[];
  /** Relatedness, claiming nothing about order: the same requirement restated in
   *  another document, or a neighbouring rule worth reading. Shown as "Vezi și".
   *  These two used to be one field labelled "Depinde de", which turned every
   *  see-also into a dependency claim — and one of them pointed the wrong way. */
  see_also: string[];
  /** The acts this rule's `norm` names, with their official text. Absent when the
   *  citation names none this validator can link to, which is the common case. */
  norm_links?: VNormLink[];
  /** The paragraph of the document's own "Rețea de dependențe" chapter that
   *  mentions this rule, quoted. Where a dependency is stated in that chapter
   *  rather than in the rule itself, this is where the architect's wording — and
   *  with it the direction — is read. Empty when the chapter does not name it. */
  order_note_ro?: string;
  notes_ro: string;
  status: VStatus;
  counts: VCounts;
  instances: VInstance[];
  error?: string;
}

/** A tally at both levels the report counts in.
 *
 *  The top level counts MEASUREMENTS — one per hall stretch, per door, per route.
 *  `base` counts ELEMENTS — one per `ref`, its status being the worst of its own
 *  measurements. The distinction is not cosmetic: 112 halls, each a conforming
 *  1,25 m trunk plus a failing 0,80 m branch, tally as "112 of 224 conform" by
 *  measurement and "0 of 112 conform" by element, and only the second is an
 *  answer to "how much of this building is compliant". */
type VTally = Record<string, number> & { total: number };

export type VCounts = VTally & {
  base: VTally;
  /** Only the rows that carry a `part` — the population `part_label` names.
   *  Absent when a rule emits no parts at all. */
  parts?: VTally;
};

export interface VChapter {
  id: string; title: string; norm: string;
  /** As on a rule. The chapter matters more, because a rule that only restates
   *  its chapter's citation prints none of its own (`extraNorm`). */
  norm_links?: VNormLink[];
}

export interface VSource {
  id: string;
  title: string;
  file: string;
  note_ro: string;
  /** The document's own heading for that chapter, number and all ("VI. Rețea de
   *  dependențe și ordine de verificare"). The drawer names the block with it
   *  rather than with a label of ours: the architect numbered the chapter, and a
   *  generic title made it impossible to see that one document's chapter was
   *  missing entirely. */
  order_title_ro?: string;
  /** A document's reading order and conflict-priority ruling. Long prose, so the
   *  drawer gives it an expandable block rather than the hover bubble `note_ro`
   *  gets — see `ORDER_RO` in the rules module. */
  order_ro?: string;
  chapters: VChapter[];
}

export interface ValidationReport {
  ok: boolean;
  elapsed_ms: number;
  scope: {
    name: string;
    storeys: number;
    flats_total: number;
    distinct_storeys: number;
    parking_levels: (number | null)[];
    has_site: boolean;
  };
  sources: VSource[];
  /** The sub-element kinds the validator derives — the only `kind` strings in
   *  the report that are neither in the solution JSON nor in `Selection`. */
  part_kinds: { kind: string; note_ro: string }[];
  /** Every act this validator can link a citation to. Shipped so what is and is
   *  not linkable is enumerable from the API, not only from the source tree —
   *  the same reason `part_kinds` is here. */
  norms?: { id: string; title: string; url: string }[];
  summary: {
    rules: number;
    neconform: number;
    necesita_confirmare: number;
    conform: number;
    inaplicabil: number;
    neverificabil: number;
    instances: number;
    elements: number;
  };
  rules: VRule[];
  /** Set when the report was fetched with `instances=0` — verdicts and counts
   *  only. The drawer then loads a rule's instances when it is opened. */
  instances_omitted?: boolean;
}

// --------------------------------------------------------------------------
// Grouping
// --------------------------------------------------------------------------

export interface RuleGroup {
  sourceId: string;
  sourceTitle: string;
  chapterId: string;
  chapterTitle: string;
  norm: string;
  /** The chapter norm's links, carried alongside the text so the heading can
   *  render them without looking the chapter up again. */
  normLinks: VNormLink[];
  rules: VRule[];
  /** Worst status in the group, for the group's own badge. */
  status: VStatus;
  failures: number;
}

const worst = (statuses: VStatus[]): VStatus =>
  statuses.length
    ? statuses.reduce((a, b) => (ROLLUP_ORDER[b] < ROLLUP_ORDER[a] ? b : a))
    : "inaplicabil";

/** Group by the source file's OWN chapters, in the file's own order.
 *
 *  Not by status, and not by a grouping of our invention: an architect checking
 *  a drawing works down the document they wrote, and a rule that moved into a
 *  "Failures" bucket is a rule they cannot find again. The badge carries the
 *  verdict; the position carries the provenance.
 *
 *  `only`, when given, keeps just the rules whose verdict is in the set — the
 *  header chips are the control. Filtering HERE rather than in the component is
 *  what makes a chapter with nothing left disappear along with its heading:
 *  `groupRules` already drops an empty chapter, and `groupSources` already drops
 *  a document with no chapters, so one argument gets the whole hierarchy right. */
export function groupRules(
  report: ValidationReport | null, only?: ReadonlySet<VStatus> | null,
): RuleGroup[] {
  if (!report) return [];
  const keep = (r: VRule) => !only || only.size === 0 || only.has(r.status);
  const out: RuleGroup[] = [];
  for (const src of report.sources) {
    for (const ch of src.chapters) {
      const rules = report.rules.filter(
        (r) => r.source === src.id && r.chapter === ch.id && keep(r),
      );
      if (!rules.length) continue;
      out.push({
        sourceId: src.id,
        sourceTitle: src.title,
        chapterId: ch.id,
        chapterTitle: ch.title,
        norm: ch.norm,
        normLinks: ch.norm_links ?? [],
        rules,
        status: worst(rules.map((r) => r.status)),
        failures: rules.filter((r) => isFailure(r.status)).length,
      });
    }
    // A rule whose chapter is not in the source's list would otherwise vanish.
    const known = new Set(src.chapters.map((c) => c.id));
    const orphans = report.rules.filter(
      (r) => r.source === src.id && !known.has(r.chapter) && keep(r),
    );
    if (orphans.length) {
      out.push({
        sourceId: src.id, sourceTitle: src.title,
        chapterId: "?", chapterTitle: t("val.fara_capitol"), norm: "", normLinks: [],
        rules: orphans, status: worst(orphans.map((r) => r.status)),
        failures: orphans.filter((r) => isFailure(r.status)).length,
      });
    }
  }
  return out;
}

/** A whole source document, with its chapters nested under it.
 *
 *  Two levels, because there will be sixteen of these documents. The file is the
 *  unit an architect thinks in ("the hall rules", "the kitchen rules") and the
 *  chapter only means anything inside it — chapter "IV" of one document has
 *  nothing to do with chapter "IV" of another, so a flat list of chapters would
 *  collide the moment the second file lands. */
export interface SourceGroup {
  id: string;
  title: string;
  file: string;
  note_ro: string;
  order_title_ro?: string;
  order_ro?: string;
  chapters: RuleGroup[];
  status: VStatus;
  failures: number;
  rules: number;
}

export function groupSources(
  report: ValidationReport | null, only?: ReadonlySet<VStatus> | null,
): SourceGroup[] {
  if (!report) return [];
  const chapters = groupRules(report, only);
  return report.sources.map((src) => {
    const mine = chapters.filter((c) => c.sourceId === src.id);
    const rules = mine.flatMap((c) => c.rules);
    return {
      id: src.id,
      title: src.title,
      file: src.file,
      note_ro: src.note_ro,
      order_title_ro: src.order_title_ro,
      order_ro: src.order_ro,
      chapters: mine,
      status: worst(rules.map((r) => r.status)),
      failures: rules.filter((r) => isFailure(r.status)).length,
      rules: rules.length,
    };
  }).filter((g) => g.chapters.length > 0);
}

/** The norm to print on a rule: only when it says something the chapter heading
 *  does not.
 *
 *  Most rules simply restate their chapter's norm, and repeating it on all
 *  twenty-one rows is noise. But it is NOT always redundant — R02 narrows it to
 *  "tabelul B", R03 to "secț. F", and R14 cites Legea 114/1996 inside a chapter
 *  headed NP 057-02. Dropping the field outright would lose exactly the citations
 *  worth reading, so the test is "does it differ", not "does it exist". */
/** One piece of a citation as it gets rendered: plain text, or an act with a
 *  link to its official text. */
export interface NormPart {
  text: string; url?: string; title?: string; isPdf?: boolean;
}

/** Split a citation into the pieces the heading renders, in order.
 *
 *  "NP 057-02, cap. 3.4.E.2; Legea 114/1996, anexa nr. 1, secț. F" becomes a plain
 *  "NP 057-02, cap. 3.4.E.2; " and a linked "Legea 114/1996, anexa nr. 1, secț. F".
 *  The reference is underlined WHERE IT STANDS rather than appended as a chip
 *  below, because a citation names up to three acts and a row of chips underneath
 *  would say each name twice and lose which subdivision belongs to which.
 *
 *  Spans come from the server and are already sorted and non-overlapping; a span
 *  that fell outside the string, or ran backwards, is skipped rather than trusted,
 *  so a bad offset costs a link and never a scrambled citation. */
export function normParts(
  text: string, links?: readonly VNormLink[] | null,
): NormPart[] {
  if (!text) return [];
  if (!links?.length) return [{ text }];
  const out: NormPart[] = [];
  let at = 0;
  for (const l of links) {
    if (l.start < at || l.end > text.length || l.end <= l.start) continue;
    if (l.start > at) out.push({ text: text.slice(at, l.start) });
    out.push({ text: text.slice(l.start, l.end), url: l.url, title: l.title,
               isPdf: l.is_pdf });
    at = l.end;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out;
}

export function extraNorm(rule: VRule, chapterNorm: string): string {
  const a = (rule.norm || "").trim();
  const b = (chapterNorm || "").trim();
  return !a || a === b ? "" : a;
}

// --------------------------------------------------------------------------
// Counting
// --------------------------------------------------------------------------

/** The tally under a rule's title, counting ELEMENTS first.
 *
 *  It used to count measurements, and on the user's own project it printed
 *  "112 din 224 conforme" for a rule that nothing passes: every hall is a
 *  conforming 1,25 m trunk plus a 0,80 m branch that fails, so exactly half the
 *  measurements are conform and not one hall is. The measurement figure is still
 *  shown, second and named — "0 din 112 holuri conforme · 112 din 224 tronsoane" —
 *  because it is the useful number once you already know the verdict, and
 *  misleading as the headline.
 *
 *  The denominator is conform + neconform. `inaplicabil` is left out of it on
 *  purpose: it means the rule's own DACĂ is false here — a recess R01 was never
 *  meant to measure — and folding exemptions into the numerator is how a
 *  validator flatters a drawing. They are reported separately, as their own
 *  count.
 */
/** Singular forms for the plural nouns the rule files carry.
 *
 *  `base_label` and `part_label` are stored plural because that is how they read
 *  in a tally, and Romanian will not accept "1 apartamente". One map here rather
 *  than a second field on all fifty rules: the set of nouns is small, closed, and
 *  changes only when a rule file introduces a new one — at which point the
 *  fallback prints the plural, which is what happens today anyway. */
const SINGULAR: Record<string, string> = {
  "apartamente": "apartament",
  "holuri": "hol",
  "holuri interioare": "hol interior",
  "băi": "baie",
  "băi interioare": "baie interioară",
  "băi cu fereastră": "baie cu fereastră",
  "camere de locuit": "cameră de locuit",
  "încăperi sanitare": "încăpere sanitară",
  "tronsoane": "tronson",
  "tronsoane de circulație": "tronson de circulație",
  "trasee": "traseu",
  "uși": "ușă",
  "pereți": "perete",
  "ferestre": "fereastră",
  "elemente": "element",
  "instanțe": "instanță",
};

/** What the elements a rule looked at ARE, by kind — for the sentence that says
 *  the rule did not apply to them.
 *
 *  `base_label` cannot be used there. It describes the rule's SCOPE, and for a
 *  rule that applies to a subset it is scope-qualified: R12's label is "băi cu
 *  fereastră", so "nu se aplică la 584 de băi cu fereastră" contradicts itself —
 *  the rule does not apply precisely because those bathrooms have no window.
 *  R24's "ferestre" and R27's "pereți" are worse: they name a thing that does not
 *  exist in those instances at all.
 *
 *  The kind is the one description that is true whether the condition held or
 *  not. */
const KIND_NOUN: Record<string, string> = {
  flat: "flat", flatRoom: "flatRoom", balcony: "balcony",
  building: "building", corridor: "corridor", bar: "bar",
};

/** Câte elemente de felul ăsta, cu substantivul la forma lui.
 *
 *  Pe FEL, nu pe eticheta regulii: felul e un cod, deci se traduce; eticheta
 *  («băi cu fereastră») e o propoziție scrisă de validator, în română. */
function cateDinFel(n: number, kind: string): string {
  const f = KIND_NOUN[kind];
  return tn(f ? (`subst.${f}#n` as CheiePlural) : "subst.element#n", n);
}

/** Câte elemente poartă eticheta pe care o dă REGULA.
 *
 *  Eticheta vine de la validator și e românească — `base_label`, `part_label`.
 *  Cât timp platforma e citită în română e și cea mai bună: e calificată pe
 *  domeniul regulii («băi cu fereastră»), iar felul singur ar spune doar
 *  «încăperi». În orice altă limbă ar fi un cuvânt românesc în mijlocul unei
 *  propoziții englezești, deci acolo se cade pe substantivul FELULUI, care se
 *  traduce. Reparația adevărată e ca validatorul să trimită un cod plus
 *  parametri — v. `docs/internationalizare.md`, axa textelor de server. */
function cateCuEticheta(n: number, eticheta: string, kind: string): string {
  return limba() === "ro" && eticheta ? roCount(n, eticheta) : cateDinFel(n, kind);
}

/** A count and its noun, in Romanian.
 *
 *  Two rules the previous version got wrong on every row. Romanian inserts "de"
 *  between the numeral and the noun when the last two digits are 00 or 20–99 —
 *  "584 de apartamente", but "112 apartamente" — and it needs the singular for 1.
 *  The report prints these counts several hundred times; getting the language
 *  wrong in a document about compliance with the law is not a small thing. */
export function roCount(n: number, plural: string): string {
  if (n === 1) return `1 ${SINGULAR[plural] ?? plural}`;
  const t = n % 100;
  const de = t === 0 ? n >= 100 : t >= 20;
  return `${n} ${de ? "de " : ""}${plural}`;
}

export function ruleTally(rule: VRule): string {
  const c = rule.counts;
  const bits: string[] = [];
  const base = c?.base;
  const baseDecided = (base?.conform ?? 0) + (base?.neconform ?? 0);
  if (baseDecided > 0) {
    bits.push(t("val.conforme_din", {
      conforme: base!.conform ?? 0,
      total: cateCuEticheta(baseDecided, rule.base_label, rule.base_kind),
    }));
  }
  // The finer figure, only when the rule really measures more than one thing per
  // element — otherwise it is the same number twice.
  // `counts.parts` when the server sent it: it counts only the rows that carry a
  // `part`, which is what `part_label` names. The per-instance tally includes the
  // dwelling-level rows a rule emits where it has no door or window to measure, so
  // labelling THAT "uși" put two populations under one noun.
  const fine = c?.parts ?? c;
  const decided = (fine?.conform ?? 0) + (fine?.neconform ?? 0);
  if (rule.part_label && decided > baseDecided) {
    bits.push(t("val.din", {
      conforme: fine.conform ?? 0,
      total: cateCuEticheta(decided, rule.part_label, rule.base_kind),
    }));
  }
  // "648 de confirmat" sat next to "584 de apartamente" and read as though the
  // "de" belonged to the numeral. The badge already says "Necesită confirmare";
  // saying the same here removes both the ambiguity and the second vocabulary.
  if (c?.necesita_confirmare) {
    bits.push(t("val.de_confirmat", { n: c.necesita_confirmare }));
  }
  if (c?.inaplicabil) {
    // "128 apartamente fără obiect" was jargon, and it was the third different
    // word for one status — the badge says "Inaplicabil", the chip said "fără
    // obiect", this said it too. Now it says what actually happened: the rule's
    // own DACĂ is false for those elements, so it did not judge them. The noun is
    // dropped when the line already names it above, which also sidesteps having
    // to agree twice in one sentence.
    // The noun is dropped when a line above already named one, which also avoids
    // agreeing twice in one sentence. Otherwise it comes from the KIND, not from
    // `base_label` — see `KIND_NOUN`.
    bits.push(bits.length > 0
      ? t("val.inaplicabil_alte", { n: c.inaplicabil })
      : t("val.inaplicabil_la",
          { substantiv: cateDinFel(c.inaplicabil, rule.base_kind) }));
  }
  if (!bits.length) {
    if (c?.total) return tn("val.instante#n", c.total);
    return t(rule.status === "neverificabil"
      ? "val.neverificabila" : "val.fara_instante");
  }
  return bits.join(" · ");
}

/** Cheia unei instanțe: ce distinge două măsurători.
 *
 *  `label` nu e unic — două tronsoane ale aceluiași hol se numesc la fel — iar
 *  poziția din listă se schimbă la re-sortare. Perechea ref+part e identitatea
 *  reală, și e aceeași aici și în URL-ul cu care Studio regăsește instanța.
 *
 *  TOATE câmpurile care identifică partea intră în cheie, nu doar `index` și
 *  `of`. Un TRASEU (HOL.R10) n-are `index`, iar `of` e holul — același hol pentru
 *  toate traseele locuinței — deci cele două-trei trasee ale unui apartament
 *  ieșeau cu aceeași cheie. Pe raportul real: 112 instanțe, 80 de chei, 32 de
 *  coliziuni. Iar `lista.find` întoarce primul rând cu cheia cerută, iar lista e
 *  sortată cu neconformele sus — deci apăsai «Living → Ușă intrare» și
 *  previzualizarea desena traseul dormitorului, cel picat. Verificarea era
 *  corectă tot timpul; se arăta altceva decât rândul ales.
 *
 *  Ce discriminează, pe fel de parte: `index` pentru tronson, `id` pentru ușă și
 *  fereastră, `door_id`/`from_id` pentru traseu, `toward_id` pentru perete. Sunt
 *  puse toate, în ordine fixă: un câmp lipsă costă un „:" gol, iar un câmp uitat
 *  costă o coliziune tăcută. */
export function instanceKey(i: VInstance | undefined | null): string {
  if (!i) return "";
  const r = i.ref as { kind?: string; id?: string; i?: number; j?: number };
  const p = i.part;
  // Fără parte, ETICHETA e singurul lucru care le desparte — și acolo chiar le
  // desparte. CONF.R04 și R05 dau șase rânduri pe aceeași locuință, câte unul
  // per poziție din tabelul B („· cameră de zi", „· bucătărie", „· depozitare"),
  // toate cu `ref`-ul apartamentului și niciun `part`, fiindcă o poziție dintr-un
  // tabel nu e un sub-element cu geometrie. 240 din 320 de rânduri se ciocneau.
  //
  // Comentariul de sus spune că eticheta NU e unică, și rămâne adevărat — dar
  // despre rândurile care au parte, unde două tronsoane ale aceluiași hol se
  // numesc la fel. Acolo partea decide și eticheta nu intră în cheie. Aici nu e
  // nimic altceva, iar o cheie care se repetă e mai rea decât una lungă.
  const part = p
    ? [p.kind, p.index ?? "", p.of ?? p.id ?? "",
       p.door_id ?? "", p.from_id ?? "", p.toward_id ?? ""].join(":")
    : i.label;
  return `${r.kind ?? ""}|${r.id ?? `${r.i ?? ""}.${r.j ?? ""}`}|${part}`;
}

/** Cadranul de sus: cât din REGISTRUL ÎNTREG e conform.
 *
 *  Numitorul e totalul regulilor, nu doar cele măsurate. Cele două răspund la
 *  întrebări diferite, și amândouă contează: „din tot ce trebuie verificat, cât
 *  e în regulă" (cifra mare) și „din ce s-a putut măsura, cât a trecut" (rândul
 *  de detaliu). Cifra mare e cea pesimistă deliberat — o regulă pe care modelul
 *  n-o poate încă verifica e muncă rămasă, nu muncă trecută. */
export interface RuleShare {
  procent: number;
  conform: number;
  total: number;
  /** Câte s-au putut măsura: conform + neconform. */
  decise: number;
}

export function ruleShare(report: ValidationReport | null): RuleShare {
  const s = report?.summary;
  if (!s) return { procent: 0, conform: 0, total: 0, decise: 0 };
  const total = s.conform + s.neconform + s.necesita_confirmare
                + s.inaplicabil + s.neverificabil;
  return {
    procent: total ? Math.round((s.conform / total) * 100) : 0,
    conform: s.conform,
    total,
    decise: s.conform + s.neconform,
  };
}

/** Bilanțul unei reguli pe soluție: cifra mare din machetă, cu numitorul ei.
 *
 *  Numărătoarea de ELEMENTE, nu de măsurători — aceeași alegere pe care o
 *  explică `ruleTally`: 112 holuri cu un tronson conform și unul picat sunt „0
 *  din 112 conforme", nu „112 din 224". Cifra mare răspunde la «cât din clădire
 *  e în regulă», iar cea fină, dedesubt, la «pe ce s-a măsurat».
 *
 *  Întoarce `null` când regula n-a decis nimic: o casetă de bilanț cu 0 din 0 ar
 *  arăta ca un eșec, când de fapt nu s-a măsurat. */
export interface RuleBalance {
  conforme: number;
  din: number;
  eticheta: string;
  /** 0..1, pentru bara de progres. */
  fractie: number;
  /** Rândul fin: măsurătorile neconforme, când regula măsoară mai multe per
   *  element. Gol altfel — altfel ar fi același număr de două ori. */
  fin: string;
  /** Pragul, exact cum l-a scris serverul. */
  prag: string;
}

export function ruleBalance(rule: VRule): RuleBalance | null {
  const c = rule.counts;
  const base = c?.base;
  const din = (base?.conform ?? 0) + (base?.neconform ?? 0);
  if (!din) return null;
  const conforme = base?.conform ?? 0;

  // „neconforme", nu „sub prag" ca în machetă: macheta desena o regulă de MINIM,
  // iar pe una de maxim „sub prag" ar numi conformitatea drept eșec. Cuvântul
  // trebuie să fie corect pe ambele feluri de prag, fiindcă amândouă există.
  const fine = c?.parts ?? c;
  const decise = (fine?.conform ?? 0) + (fine?.neconform ?? 0);
  const fin = rule.part_label && decise > din
    ? `${fine.neconform ?? 0} din ${roCount(decise, rule.part_label)} neconforme`
    : "";

  return {
    conforme,
    din,
    eticheta: rule.base_label || "elemente",
    fractie: conforme / din,
    fin,
    prag: rule.required?.display ?? "",
  };
}

/** Măsurat și prag, în DOUĂ coloane — macheta le desparte, iar despărțite se pot
 *  citi pe verticală: o coloană de valori măsurate lângă una de praguri arată
 *  imediat cât de departe e fiecare. `measuredVsRequired` le lipește pentru
 *  locurile cu o singură coloană; ambele citesc aceleași câmpuri. */
/** Unitatea unui rând: a instanței, apoi a regulii, apoi metri.
 *
 *  `??` și nu `||`. Un raport arie/arie e ADIMENSIONAL, iar regula îi pune
 *  `unit: ""` ca s-o spună — dar `""` e falsy, deci lanțul cădea pe „m" și
 *  BUC.R14 scria „0,43 m" pentru ceea ce propoziția de dedesubt numește „1/2.3".
 *  Un șir gol pus explicit e un răspuns, nu o valoare lipsă. */
function unitOf(inst: VInstance, rule: VRule): string {
  return inst.unit ?? rule.required?.unit ?? "m";
}

export function instMeasured(inst: VInstance, rule: VRule): string {
  if (inst.measured == null) return "—";
  return fmtRo(inst.measured, unitOf(inst, rule));
}

export function instRequired(inst: VInstance, rule: VRule): string {
  // `|| "—"` and not `?? "—"`. `Required.__post_init__` leaves `display` as the
  // EMPTY STRING when a rule fixes no threshold, and "" is not nullish — so the
  // cell rendered blank on thirty-one rules, which reads as a missing value
  // rather than as "the law sets no threshold here". HOL.R02 is the case that
  // makes it plain: its own sentence is "nu se preia o cifră de suprafață minimă
  // pentru hol, aceasta nefiind fixată de lege".
  if (inst.required == null) return rule.required?.display || "—";
  // Un prag de INTERVAL nu se poate scrie ca o singură margine. Instanța trimite
  // capătul de jos, iar „min. 0,12" spunea că 0,43 trece — pe un rând marcat
  // Neconform, care pică fiindcă depășește capătul de sus. Intervalul se scrie
  // întreg, cum l-a scris regula: „1/8–1/6 din aria pardoselii".
  if (rule.required?.kind === "range" && rule.required.display) {
    return rule.required.display;
  }
  const kind = rule.required?.kind === "max" ? "max." : "min.";
  return `${kind} ${fmtRo(inst.required, unitOf(inst, rule))}`;
}

/** How many ELEMENTS a rule found non-conforming — the number worth putting on a
 *  chapter or document badge, for the same reason `ruleTally` leads with it. */
export function ruleBad(rule: VRule): number {
  return rule.counts?.base?.neconform ?? 0;
}

/** Instances worth showing first: failures, then things to confirm, then the
 *  rest. The server already sorts this way; re-applying it here keeps the drawer
 *  correct if instances arrive from a per-rule fetch in another order. */
export function sortInstances(list: VInstance[]): VInstance[] {
  return [...list].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
  );
}

/** How the measurement reads on an instance row: "0,80 m / min. 1,20 m". */
export function measuredVsRequired(inst: VInstance, rule: VRule): string {
  if (inst.measured == null) return rule.required?.display ?? "";
  const unit = inst.unit || rule.required?.unit || "m";
  const m = fmtRo(inst.measured, unit);
  if (inst.required == null) return m;
  const kind = rule.required?.kind === "max" ? "max." : "min.";
  return `${m} / ${kind} ${fmtRo(inst.required, unit)}`;
}

/** Romanian number: decimal comma, two places, unit appended. Matches
 *  `validation/model.py ro()` so a row and its detail text never disagree. */
export function fmtRo(x: number, unit = "m", nd = 2): string {
  const s = x.toFixed(nd).replace(".", ",");
  return unit ? `${s} ${unit}` : s;
}

/** The badge for the toolbar button: how many rules need attention. */
export function attentionCount(report: ValidationReport | null): number {
  if (!report) return 0;
  return report.rules.filter((r) => needsAttention(r.status)).length;
}

/** One line summarising what was validated, for the drawer header. */
export function scopeLine(report: ValidationReport | null): string {
  if (!report) return "";
  const s = report.scope;
  const bits = [tn("val.scop.apartamente#n", s.flats_total),
                tn("val.scop.niveluri#n", s.storeys)];
  if (s.distinct_storeys === 1 && s.storeys > 1) {
    bits.push(t("val.scop.etaje_identice"));
  }
  // Elements first, measurements second — the same order the per-rule tally
  // uses, and the same reason: the element is what gets signed off.
  bits.push(tn("val.scop.elemente#n", report.summary.elements));
  bits.push(tn("val.scop.masuratori#n", report.summary.instances));
  return bits.join(" · ");
}

// --------------------------------------------------------------------------
// The header chips, which are also the filter
// --------------------------------------------------------------------------

export interface StatusChip {
  status: VStatus;
  /** Rules in this state. */
  n: number;
  label: string;
  /** Nothing to filter to — rendered flat, not as a button. */
  empty: boolean;
}

/** One chip per verdict, in worst-first order, each a filter control.
 *
 *  All five are always present, including the zeroes, and that is deliberate on
 *  two counts. A chip that appears only when it is non-zero moves the others
 *  every time a re-validate changes a count, so the thing the user is aiming at
 *  is somewhere else by the time they click. And "0 neconforme" is the single
 *  most useful readout in the panel — a row that vanishes when it reaches zero
 *  is a row that never tells you the good news.
 *
 *  `conform` earns its place for the same reason: without it the header showed
 *  what was wrong and left "how much is right" to be worked out by subtraction. */
export function statusChips(report: ValidationReport | null): StatusChip[] {
  if (!report) return [];
  const s = report.summary;
  const n: Record<VStatus, number> = {
    neconform: s.neconform,
    necesita_confirmare: s.necesita_confirmare,
    conform: s.conform,
    inaplicabil: s.inaplicabil,
    neverificabil: s.neverificabil,
  };
  const word: Record<VStatus, string> = {
    neconform: "neconforme",
    necesita_confirmare: "de confirmat",
    conform: "conforme",
    // Same word family as the badge and the tally. "fără obiect" was the odd
    // one out among chips that otherwise all use the adjective — neconforme,
    // conforme, neverificabile.
    inaplicabil: "inaplicabile",
    neverificabil: "neverificabile",
  };
  return (["neconform", "necesita_confirmare", "conform", "inaplicabil",
           "neverificabil"] as VStatus[]).map((st) => ({
    status: st,
    n: n[st],
    label: st === "neconform" && n[st] === 0
      ? t("val.nicio_neconformitate")
      : `${n[st]} ${word[st]}`,
    empty: n[st] === 0,
  }));
}

/** Toggle one status in the filter set. An empty set means "no filter", which is
 *  also what clicking the last active chip off gives you — so the control can
 *  always be undone by the same gesture that set it. */
/** Is a collapsible group open?
 *
 *  Two inputs, and the second is the one that matters. `explicit` is what the user
 *  last did to THIS group (undefined until they touch it); `filtered` says whether
 *  a status filter is on.
 *
 *  Everything starts collapsed, because with eight documents and fifty chapters an
 *  expanded tree is a wall of text before the first click — what the panel should
 *  open on is the list of groups.
 *
 *  But a filter inverts that. Clicking "9 reguli neconforme" is a request to SEE
 *  those nine, and answering it with eight collapsed rows would make the chip look
 *  broken. So while a filter is on, groups default to open — and an explicit toggle
 *  still wins over both defaults, so a user who closes a group during a filter
 *  keeps it closed.
 */
export function groupIsOpen(
  explicit: boolean | undefined, filtered: boolean,
): boolean {
  return explicit ?? filtered;
}

export function toggleStatus(
  cur: ReadonlySet<VStatus>, st: VStatus,
): Set<VStatus> {
  const next = new Set(cur);
  if (next.has(st)) next.delete(st); else next.add(st);
  return next;
}

/** What the filter is showing, in words, for the line that offers to clear it. */
export function filterLine(only: ReadonlySet<VStatus>, shown: number): string {
  if (!only.size) return "";
  const names = (["neconform", "necesita_confirmare", "conform", "inaplicabil",
                  "neverificabil"] as VStatus[])
    .filter((s) => only.has(s))
    .map((s) => numeleVerdictului(s).toLowerCase());
  return `${tn("val.filtru_reguli#n", shown)} · ${names.join(", ")}`;
}

// --------------------------------------------------------------------------
// Resolving a Selection back to geometry
// --------------------------------------------------------------------------

/** The ring of the element a Selection points at, on one apartment storey.
 *
 *  This is the other half of the size decision: the server omits polygons the UI
 *  can look up, and this is the lookup. The level is typed loosely on purpose —
 *  a structural parameter, not an import of `ApartmentLevel` — because
 *  `validation.ts` is imported by `client.ts` and taking the type from there
 *  would close a cycle.
 *
 *  Returns null when the selection points past the end of the storey on screen,
 *  which happens legitimately: a report is indexed positionally, and a re-solve
 *  can replace the plan under a held reference. The annotation then simply does
 *  not draw, the same way `describeSelection` returns null and the inspector
 *  closes. */
/** The level shape both lookups below need. Typed structurally rather than
 *  imported from `plan/ApartmentView`, because `validation.ts` is imported by
 *  `client.ts` and taking the type from there would close a cycle. */
export interface IdLevel {
  flats?: ReadonlyArray<{
    eid?: string;
    layout?: ReadonlyArray<{ eid?: string }>;
    balcony?: { eid?: string } | null;
  }>;
  buildings?: ReadonlyArray<{ eid?: string }>;
  cores?: ReadonlyArray<{ eid?: string; parts?: ReadonlyArray<{ eid?: string }> }>;
}

/** Resolve a report ref to the selection the viewer actually uses, BY ID.
 *
 *  This is the whole point of carrying ids: the report says
 *  `{kind: "flatRoom", i: 12, j: 0, id: "rm_d1f68…"}` and this finds where
 *  `rm_d1f68…` lives in the plan on screen right now. The indices in the ref are
 *  where it lived when the report was computed, which is the same thing until
 *  something re-solves and then silently is not.
 *
 *  Returns null when the ref has no id (a legacy document — the caller falls back
 *  to the indices) and, separately, when the id is not on this level at all. The
 *  second case is a STALE report: the element it judged no longer exists. The
 *  caller must tell those two apart, which is why this returns null rather than
 *  quietly handing back the positional ref.
 *
 *  Only the kinds the hall rules emit are resolved. Adding a rule that points at
 *  a bar or a corridor means adding its case here; until then a `null` is honest
 *  and a wrong highlight would not be. */
export function selectionOfId(
  ref: VRef | null | undefined, level: IdLevel | null | undefined,
): Selection | null {
  if (!ref?.id || !level) return null;
  const id = ref.id;
  switch (ref.kind) {
    case "flat": {
      const i = (level.flats ?? []).findIndex((f) => f.eid === id);
      return i < 0 ? null : { kind: "flat", level: ref.level, i };
    }
    case "flatRoom": {
      const flats = level.flats ?? [];
      for (let i = 0; i < flats.length; i++) {
        const j = (flats[i].layout ?? []).findIndex((r) => r.eid === id);
        if (j >= 0) return { kind: "flatRoom", level: ref.level as number, i, j };
      }
      return null;
    }
    case "balcony": {
      // The document keeps a balcony in `rooms`; the viewer keeps it as a field
      // on the dwelling. So the id is the room's and the index is the flat's.
      const i = (level.flats ?? []).findIndex((f) => f.balcony?.eid === id);
      return i < 0 ? null : { kind: "balcony", level: ref.level as number, i };
    }
    case "building": {
      const i = (level.buildings ?? []).findIndex((b) => b.eid === id);
      return i < 0 ? null : { kind: "building", level: ref.level as number, i };
    }
    case "corePart": {
      const cores = level.cores ?? [];
      for (let i = 0; i < cores.length; i++) {
        const j = (cores[i].parts ?? []).findIndex((p) => p.eid === id);
        if (j >= 0) return { kind: "corePart", level: ref.level as number, i, j };
      }
      return null;
    }
    default:
      return null;
  }
}

/** Which selection to actually hand the plan, and whether the report is stale.
 *
 *  `stale` is the case worth having a name for: the ref carries an id, the level
 *  carries ids, and the id is not there. That means the report describes a plan
 *  this is no longer — so the honest move is to say so, not to fall back to the
 *  indices and light up whatever now happens to sit at position 12. The old
 *  behaviour was exactly that fallback, with nothing to distinguish it from a
 *  correct hit. */
export function resolveRef(
  ref: VRef | null | undefined, level: IdLevel | null | undefined,
): { sel: Selection | null; stale: boolean } {
  if (!ref) return { sel: null, stale: false };
  if (!ref.id) return { sel: ref as Selection, stale: false };   // legacy: no ids to resolve
  const byId = selectionOfId(ref, level);
  if (byId) return { sel: byId, stale: false };
  // The level has ids of its own, so the lookup was meaningful and it failed.
  const hasIds = (level?.flats ?? []).some((f) => !!f.eid);
  return hasIds ? { sel: null, stale: true } : { sel: ref as Selection, stale: false };
}

export function ringOfSelection(
  sel: Selection | null | undefined,
  level: {
    flats?: ReadonlyArray<{
      poly?: number[][];
      layout?: ReadonlyArray<{ poly?: number[][] }>;
      balcony?: { poly?: number[][] } | null;
    }>;
    bars?: ReadonlyArray<{ poly?: number[][]; corridor?: number[][] }>;
    buildings?: ReadonlyArray<{ poly?: number[][] }>;
    cores?: ReadonlyArray<{ parts?: ReadonlyArray<{ poly?: number[][] }> }>;
  } | null | undefined,
): number[][] | null {
  if (!sel || !level) return null;
  const j = (sel as { j?: number }).j;
  switch (sel.kind) {
    case "flat":
      return level.flats?.[sel.i]?.poly ?? null;
    case "flatRoom":
      return j == null ? null : level.flats?.[sel.i]?.layout?.[j]?.poly ?? null;
    case "balcony":
      return level.flats?.[sel.i]?.balcony?.poly ?? null;
    case "bar":
      return level.bars?.[sel.i]?.poly ?? null;
    case "corridor":
      return level.bars?.[sel.i]?.corridor ?? null;
    case "building":
      return level.buildings?.[sel.i]?.poly ?? null;
    case "corePart":
      return j == null ? null : level.cores?.[sel.i]?.parts?.[j]?.poly ?? null;
    default:
      return null;
  }
}
